import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  loadFacilities,
  loadFacilitiesAndSetCurrentFacilityById,
  loadFacilitiesAndSetCurrentFacilityByIdSuccess,
  loadFacilitiesAtStartup,
  loadFacilitiesAtStartupError,
  loadFacilitiesAtStartupSuccess,
  loadFacilitiesError,
  loadFacilitiesSuccess,
  loadFacilityById,
  loadFacilityByIdError,
  loadFacilityByIdSuccess,
  loadFacilityGroups,
  loadFacilityGroupsError,
  loadFacilityGroupsSuccess,
  loadFacilityPerformanceBuildingByFacilityId,
  loadFacilityPerformanceBuildingByFacilityIdError,
  loadFacilityPerformanceBuildingByFacilityIdSuccess,
  loadFacilityPerformancesBuildingByFacilityGroupId,
  loadFacilityPerformancesBuildingByFacilityGroupIdError,
  loadFacilityPerformancesBuildingByFacilityGroupIdSuccess,
  loadFacilityPerformanceSolarByFacilityGroupId,
  loadFacilityPerformanceSolarByFacilityGroupIdError,
  loadFacilityPerformanceSolarByFacilityGroupIdSuccess,
  loadFacilityPerformanceSolarByFacilityId,
  loadFacilityPerformanceSolarByFacilityIdError,
  loadFacilityPerformanceSolarByFacilityIdSuccess,
  setCurrentFacilityById,
  updateFacility,
  updateFacilityError,
  updateFacilityGroup,
  updateFacilityGroupError,
  updateFacilityGroupSuccess,
  addFacilityBuildings,
  addFacilityBuildingsSuccess,
  addFacilityBuildingsError,
  updateFacilityBuildings,
  updateFacilityBuildingsSuccess,
  updateFacilityBuildingsError,
  deleteFacilityBuildings,
  deleteFacilityBuildingsSuccess,
  deleteFacilityBuildingsError,
  linkUtilityContractsToBuilding,
  linkUtilityContractsToBuildingSuccess,
  linkUtilityContractsToBuildingError,
  unlinkUtilityContractsFromBuilding,
  unlinkUtilityContractsFromBuildingSuccess,
  unlinkUtilityContractsFromBuildingError,
  createFacilityGroupsFromOnboarding,
  createFacilityGroupsFromOnboardingSuccess,
  createFacilityGroupsFromOnboardingError,
  createFacility,
  createFacilitySuccess,
  createFacilityError,
} from '../actions/facility.actions';
import { catchError, forkJoin, map, mergeMap, of, switchMap } from 'rxjs';
import { FacilityGroupService } from '../../service/facility-group/facility-group.service';
import { FacilityService } from '../../service/facility/facility.service';
import { FacilityPerformanceService } from 'brain-data/service/facility-performance/facility-performance.service';
import { BuildingService } from 'brain-data/service/facility/building.service';
import { BuildingUtilityContractService } from 'brain-data/service/facility/building-utility-contract.service';
import { postLoginSuccess } from '../actions/authentication.action';
import { FacilityGroup } from 'brain-data/model/facility-group.model';

@Injectable()
export class FacilityEffects {
  //Facilities

  loadFacilityGroupsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilityGroups),
      mergeMap(() =>
        this.facilityGroupService.getFacilityGroups().pipe(
          map(facilityGroups => loadFacilityGroupsSuccess({ facilityGroups })),
          catchError(error => of(loadFacilityGroupsError({ error }))),
        ),
      ),
    );
  });

  loadFacilitiesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilities),
      switchMap(() =>
        this.facilityService.getFacilities().pipe(
          map(facilities => loadFacilitiesSuccess({ facilities })),
          catchError(error => of(loadFacilitiesError({ error }))),
        ),
      ),
    );
  });

  createFacilityEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createFacility),
      switchMap(({ request }) =>
        this.facilityService.createFacility(request).pipe(
          map(facility => createFacilitySuccess({ facility })),
          catchError(error => of(createFacilityError({ error }))),
        ),
      ),
    );
  });

  createFacilitySuccessEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createFacilitySuccess),
      map(() => loadFacilities()),
    );
  });

  loadFacilitiesAndSetCurrentFacilityByIdEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilitiesAndSetCurrentFacilityById),
      switchMap(({ facilityId }) =>
        this.facilityService.getFacilities().pipe(
          map(facilities => loadFacilitiesAndSetCurrentFacilityByIdSuccess({ facilities, facilityId })),
          catchError(error => of(loadFacilitiesError({ error }))),
        ),
      ),
    );
  });

  loadFacilitiesAndSetCurrentFacilityByIdSuccessEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilitiesAndSetCurrentFacilityByIdSuccess),
      map(({ facilityId }) => setCurrentFacilityById({ facilityId })),
    );
  });

  loadFacilityByIdEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilityById),
      mergeMap(({ facilityId }) =>
        this.facilityService.getFacilityById(facilityId).pipe(
          map(facility => loadFacilityByIdSuccess({ facility })),
          catchError(error => of(loadFacilityByIdError({ error }))),
        ),
      ),
    );
  });

  loadFacilityPerformanceBuildingByFacilityGroupIdEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilityPerformancesBuildingByFacilityGroupId),
      switchMap(({ facilityGroupId }) =>
        this.facilityPerformanceService.getFacilityPerformancesBuildingByFacilityGroupId(facilityGroupId).pipe(
          map(facilityGroupPerformance => loadFacilityPerformancesBuildingByFacilityGroupIdSuccess({ facilityGroupPerformance })),
          catchError(error => of(loadFacilityPerformancesBuildingByFacilityGroupIdError({ error }))),
        ),
      ),
    );
  });

  loadFacilityPerformanceBuildingByFacilityIdEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilityPerformanceBuildingByFacilityId),
      switchMap(({ facilityId }) =>
        this.facilityPerformanceService.getFacilityPerformancesBuildingByFacilityId(facilityId).pipe(
          map(facilityPerformance => loadFacilityPerformanceBuildingByFacilityIdSuccess({ facilityPerformance })),
          catchError(error => of(loadFacilityPerformanceBuildingByFacilityIdError({ error }))),
        ),
      ),
    );
  });

  loadFacilityPerformanceSolarByFacilityGroupIdEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilityPerformanceSolarByFacilityGroupId),
      switchMap(({ facilityGroupId }) =>
        this.facilityPerformanceService.getFacilityPerformancesSolarByFacilityGroupId(facilityGroupId).pipe(
          map(facilitySolarPerformance => loadFacilityPerformanceSolarByFacilityGroupIdSuccess({ facilitySolarPerformance })),
          catchError(error => of(loadFacilityPerformanceSolarByFacilityGroupIdError({ error }))),
        ),
      ),
    );
  });

  loadFacilityPerformanceSolarByFacilityIdEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilityPerformanceSolarByFacilityId),
      switchMap(({ facilityId }) =>
        this.facilityPerformanceService.getFacilityPerformancesSolarByFacilityId(facilityId).pipe(
          map(facilitySolarPerformance => loadFacilityPerformanceSolarByFacilityIdSuccess({ facilitySolarPerformance })),
          catchError(error => of(loadFacilityPerformanceSolarByFacilityIdError({ error }))),
        ),
      ),
    );
  });

  updateFacilityGroupEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateFacilityGroup),
      switchMap(({ facilityGroupId, updateFacilityGroupRequest }) =>
        this.facilityGroupService.updateFacilityGroup(facilityGroupId, updateFacilityGroupRequest).pipe(
          map(() =>
            updateFacilityGroupSuccess({
              facilityGroupId,
              updatedFacilityGroup: {
                id: facilityGroupId,
                description: updateFacilityGroupRequest.description,
                name: updateFacilityGroupRequest.name,
                icon: updateFacilityGroupRequest.icon,
                iconColor: updateFacilityGroupRequest.iconColor,
              } as FacilityGroup,
            }),
          ),
          catchError(error => of(updateFacilityGroupError({ error }))),
        ),
      ),
    );
  });

  //Combined Actions
  loadDataAfterLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postLoginSuccess),
      mergeMap(() => of(loadFacilitiesAtStartup())),
    );
  });

  loadFacilitiesAtStartupEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadFacilitiesAtStartup),
      switchMap(() =>
        forkJoin([this.facilityService.getFacilities(), this.facilityGroupService.getFacilityGroups()]).pipe(
          map(([facilities, facilityGroups]) =>
            loadFacilitiesAtStartupSuccess({
              facilities,
              facilityGroups,
            }),
          ),
          catchError(error => of(loadFacilitiesAtStartupError({ error }))),
        ),
      ),
    );
  });

  updateFacilityEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateFacility),
      switchMap(({ facilityId, facility }) =>
        this.facilityService.updateFacility(facilityId, facility).pipe(
          map(() => loadFacilitiesAndSetCurrentFacilityById({ facilityId })),
          catchError(error => of(updateFacilityError({ error }))),
        ),
      ),
    );
  });

  addFacilityBuildingsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addFacilityBuildings),
      switchMap(({ facilityId, buildings }) =>
        this.buildingService
          .addBuildings(
            facilityId,
            buildings.map(b => ({
              name: b.name,
              latitude: b.latitude,
              longitude: b.longitude,
              yearBuilt: b.yearBuilt,
              grossArea: b.grossArea,
              vacant: b.vacant,
            })),
          )
          .pipe(
            map(blds => addFacilityBuildingsSuccess({ facilityId, buildings: blds })),
            catchError(error => of(addFacilityBuildingsError({ error }))),
          ),
      ),
    );
  });

  updateFacilityBuildingsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateFacilityBuildings),
      switchMap(({ facilityId, buildings }) =>
        this.buildingService
          .updateBuildings(
            facilityId,
            buildings.map(b => ({
              id: b.id,
              name: b.name,
              latitude: b.latitude,
              longitude: b.longitude,
              yearBuilt: b.yearBuilt,
              grossArea: b.grossArea,
              vacant: b.vacant,
            })),
          )
          .pipe(
            map(blds => updateFacilityBuildingsSuccess({ facilityId, buildings: blds })),
            catchError(error => of(updateFacilityBuildingsError({ error }))),
          ),
      ),
    );
  });

  deleteFacilityBuildingsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteFacilityBuildings),
      switchMap(({ facilityId, ids }) =>
        this.buildingService.deleteBuildings(facilityId, ids).pipe(
          map(() => deleteFacilityBuildingsSuccess({ facilityId, ids })),
          catchError(error => of(deleteFacilityBuildingsError({ error }))),
        ),
      ),
    );
  });

  linkUtilityContractsToBuildingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(linkUtilityContractsToBuilding),
      switchMap(({ facilityId, buildingId, contractNumbers }) =>
        this.buildingUtilityContractService.linkContractsToBuilding(buildingId, facilityId, contractNumbers).pipe(
          map(() => linkUtilityContractsToBuildingSuccess()),
          catchError(error => of(linkUtilityContractsToBuildingError({ error }))),
        ),
      ),
    );
  });

  unlinkUtilityContractsFromBuildingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(unlinkUtilityContractsFromBuilding),
      switchMap(({ contractNumbers }) =>
        this.buildingUtilityContractService.unlinkContractsFromBuilding(contractNumbers).pipe(
          map(() => unlinkUtilityContractsFromBuildingSuccess()),
          catchError(error => of(unlinkUtilityContractsFromBuildingError({ error }))),
        ),
      ),
    );
  });

  createFacilityGroupsFromOnboardingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createFacilityGroupsFromOnboarding),
      switchMap(({ groups }) =>
        this.facilityGroupService.createFacilityGroupsFromOnboarding(groups).pipe(
          map(() => createFacilityGroupsFromOnboardingSuccess()),
          catchError(error => of(createFacilityGroupsFromOnboardingError({ error }))),
        ),
      ),
    );
  });

  refreshFacilitiesAfterOnboardingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createFacilityGroupsFromOnboardingSuccess),
      map(() => loadFacilitiesAtStartup()),
    );
  });

  refreshFacilitiesAfterLinkUnlinkEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(linkUtilityContractsToBuildingSuccess, unlinkUtilityContractsFromBuildingSuccess),
      map(() => loadFacilitiesAtStartup()),
    );
  });

  constructor(
    private actions$: Actions,
    private facilityGroupService: FacilityGroupService,
    private facilityService: FacilityService,
    private facilityPerformanceService: FacilityPerformanceService,
    private buildingService: BuildingService,
    private buildingUtilityContractService: BuildingUtilityContractService,
  ) {}
}
